import { React, useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Card,
	Row,
	Col,
	Tag,
	Button,
	Spin,
	message,
	Typography,
	Space,
	Avatar,
} from "antd";
import {
	ArrowLeftOutlined,
	UserOutlined,
	CalendarOutlined,
	PhoneOutlined,
	HomeOutlined,
	TeamOutlined,
	FileTextOutlined,
	EditOutlined,
	HeartOutlined,
	HistoryOutlined,
} from "@ant-design/icons";
import {
	ViewClassificationChangeRecordsModal,
	ViewStatusChangeRecordsModal,
	ViewBaptismalStatusChangeRecordsModal,
	ViewCellChangeRecordsModal,
	ViewAffiliatedDepartmentChangeRecordsModal,
	ViewDisciplinaryRecordsModal,
	ViewServingDepartmentChangeRecordsModal,
	ViewTransferRecordsModal,
} from "./index";

const { Title, Text } = Typography;

const ViewMemberDetail = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [memberData, setMemberData] = useState(null);
	const [loading, setLoading] = useState(true);

	// Modal visibility states
	const [classificationRecordsVisible, setClassificationRecordsVisible] =
		useState(false);
	const [statusRecordsVisible, setStatusRecordsVisible] = useState(false);
	const [
		baptismalStatusRecordsVisible,
		setBaptismalStatusRecordsVisible,
	] = useState(false);
	const [cellRecordsVisible, setCellRecordsVisible] = useState(false);
	const [departmentRecordsVisible, setDepartmentRecordsVisible] =
		useState(false);
	const [disciplinaryRecordsVisible, setDisciplinaryRecordsVisible] =
		useState(false);
	const [
		servingDepartmentRecordsVisible,
		setServingDepartmentRecordsVisible,
	] = useState(false);
	const [transferRecordsVisible, setTransferRecordsVisible] =
		useState(false);

	const getMemberDetail = useCallback(async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/users/user-detail/${id}/`
			);
			if (response.status === 200) {
				setMemberData(response.data);
			}
		} catch (error) {
			message.error("성도 정보를 불러오는데 실패했습니다.");
			navigate("../view-all-members");
		} finally {
			setLoading(false);
		}
	}, [id, navigate]);

	useEffect(() => {
		getMemberDetail();
	}, [getMemberDetail]);

	const handleBack = () => {
		navigate("../view-all-members");
	};

	const handleEdit = () => {
		navigate(`../edit-member/${id}`);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("ko-KR");
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "입교인":
				return "blue";
			case "학습인":
				return "green";
			case "유아세례교인":
				return "orange";
			case "실종교인":
				return "red";
			case "별세교인":
				return "default";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const getGenderColor = (gender) => {
		switch (gender) {
			case "남":
				return "blue";
			case "여":
				return "pink";
			default:
				return "default";
		}
	};

	const InfoItem = ({
		label,
		value,
		icon,
		tagColor,
		isTag = false,
		isMultiline = false,
	}) => (
		<div className="member-detail__info-item">
			<Text className="member-detail__info-label">
				{label}:
			</Text>
			<div className="member-detail__info-value">
				{icon && (
					<span className="member-detail__info-icon">
						{icon}
					</span>
				)}
				{isTag ? (
					<Tag color={tagColor}>
						{value || "-"}
					</Tag>
				) : isMultiline ? (
					<div style={{ whiteSpace: "pre-line" }}>
						<Text strong>
							{value || "-"}
						</Text>
					</div>
				) : (
					<Text strong>{value || "-"}</Text>
				)}
			</div>
		</div>
	);

	const FamilyMemberCard = ({
		member,
		relationship,
		onClick,
		isSpouse = false,
	}) => (
		<Card
			size="small"
			className="family-member-card"
			onClick={onClick}
			style={{
				cursor: onClick ? "pointer" : "default",
				border: isSpouse
					? "1px solid #d9d9d9"
					: undefined,
			}}>
			<div style={{ textAlign: "center" }}>
				<Avatar
					size={isSpouse ? 30 : 40}
					icon={<UserOutlined />}
					style={{
						backgroundColor:
							member.gender === "남"
								? "#2457e6"
								: "#eb2f96",
						marginBottom: 8,
					}}
				/>
				<div
					style={{
						fontSize: isSpouse
							? "10px"
							: "12px",
						fontWeight: "bold",
					}}>
					{member.name}
				</div>
				<div
					style={{
						fontSize: isSpouse
							? "8px"
							: "10px",
						color: "#666",
					}}>
					{member.birthdate
						? new Date(
								member.birthdate
						  ).toLocaleDateString("ko-KR")
						: "-"}
				</div>
				<Tag size="small" color="blue">
					{relationship}
				</Tag>
			</div>
		</Card>
	);

	const ChildWithSpouseCard = ({ child, onClick }) => (
		<div style={{ textAlign: "center" }}>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				{child.gender === "남" ? (
					<>
						<FamilyMemberCard
							member={child}
							relationship="자녀"
							onClick={() =>
								onClick(
									child.id
								)
							}
						/>
						{child.spouse && (
							<>
								<div className="spouse-connector"></div>
								<FamilyMemberCard
									member={
										child.spouse
									}
									relationship="배우자"
									onClick={() =>
										onClick(
											child
												.spouse
												.id
										)
									}
									isSpouse={
										true
									}
								/>
							</>
						)}
					</>
				) : (
					<>
						{child.spouse && (
							<>
								<FamilyMemberCard
									member={
										child.spouse
									}
									relationship="배우자"
									onClick={() =>
										onClick(
											child
												.spouse
												.id
										)
									}
									isSpouse={
										true
									}
								/>

								<div className="spouse-connector"></div>
							</>
						)}

						<FamilyMemberCard
							member={child}
							relationship="자녀"
							onClick={() =>
								onClick(
									child.id
								)
							}
						/>
					</>
				)}
			</div>
		</div>
	);

	const handleFamilyMemberClick = (memberId) => {
		navigate(`../view-member-detail/${memberId}`);
	};

	if (loading) {
		return (
			<div className="member-detail__loading">
				<Spin size="large" />
				<Text>성도 정보를 불러오는 중...</Text>
			</div>
		);
	}

	if (!memberData) {
		return (
			<div className="member-detail__error">
				<Text type="danger">
					성도 정보를 찾을 수 없습니다.
				</Text>
				<Button onClick={handleBack}>
					목록으로 돌아가기
				</Button>
			</div>
		);
	}

	// Sort parents: father (male) on left, mother (female) on right
	const sortedParents = memberData.parents
		? memberData.parents.sort((a, b) => {
				if (a.gender === "남" && b.gender === "여")
					return -1;
				if (a.gender === "여" && b.gender === "남")
					return 1;
				return 0;
		  })
		: [];

	// Sort spouse's parents: father (male) on left, mother (female) on right
	const sortedSpouseParents = memberData.spouse?.parents
		? memberData.spouse.parents.sort((a, b) => {
				if (a.gender === "남" && b.gender === "여")
					return -1;
				if (a.gender === "여" && b.gender === "남")
					return 1;
				return 0;
		  })
		: [];

	// Sort children by age (birthdate)
	const sortedChildren = memberData.children
		? memberData.children.sort((a, b) => {
				if (!a.birthdate) return 1;
				if (!b.birthdate) return -1;
				return (
					new Date(a.birthdate) -
					new Date(b.birthdate)
				);
		  })
		: [];

	return (
		<>
			<PageTitle title="성도 상세정보" />
			<div className="member-detail__container">
				<div className="member-detail__header">
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={handleBack}
						className="member-detail__back-button">
						목록으로 돌아가기
					</Button>
					<Title
						level={2}
						className="member-detail__title">
						성도 상세정보
					</Title>
					<Button
						icon={<EditOutlined />}
						onClick={handleEdit}
						className="member-detail__edit-button">
						수정
					</Button>
				</div>

				<div className="member-detail__content">
					{/* 기본 정보 섹션 */}
					<Card
						title={
							<Space>
								<UserOutlined />
								기본 정보
							</Space>
						}
						className="member-detail__card">
						<Row gutter={[24, 16]}>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="ID"
									value={
										memberData.id
									}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="성명"
									value={
										memberData.nickname
											? memberData.name +
											  "(" +
											  memberData.nickname +
											  ")"
											: memberData.name
									}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="성별"
									value={
										memberData.gender
									}
									tagColor={getGenderColor(
										memberData.gender
									)}
									isTag={
										true
									}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="생년월일"
									value={formatDate(
										memberData.birthdate
									)}
									icon={
										<CalendarOutlined />
									}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="연락처"
									value={
										memberData.phone_number
									}
									icon={
										<PhoneOutlined />
									}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="직업"
									value={
										memberData.job
									}
								/>
							</Col>
						</Row>
						<Row
							gutter={[24, 16]}
							style={{
								marginTop: 16,
							}}>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<div className="member-detail__info-item">
									<Text className="member-detail__info-label">
										인도자
										ID:
									</Text>
									<div className="member-detail__info-value">
										<Text
											strong>
											{memberData.led_by_id ||
												"-"}
										</Text>
										{memberData.led_by_id && (
											<Button
												type="primary"
												size="small"
												icon={
													<UserOutlined />
												}
												onClick={() =>
													navigate(
														`../view-member-detail/${memberData.led_by_id}`
													)
												}
												style={{
													marginLeft: 8,
												}}
												title="인도자 상세정보 보기">
												이동
											</Button>
										)}
									</div>
								</div>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="인도자"
									value={
										memberData.led_by_name
									}
								/>
							</Col>
						</Row>
					</Card>
					{memberData.spouse && (
						<Card
							title={
								<Space>
									<TeamOutlined />
									배우자
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}>
									<InfoItem
										label="배우자"
										value={
											memberData
												.spouse
												?.name
												? memberData
														.spouse
														.name +
												  "(" +
												  memberData
														.spouse
														.id +
												  ")"
												: "-"
										}
									/>
								</Col>
								<Col
									xs={24}
									sm={12}>
									<Button
										type="primary"
										size="small"
										icon={
											<UserOutlined />
										}
										onClick={() =>
											navigate(
												`../view-member-detail/${memberData.spouse.id}`
											)
										}
										title="배우자 상세정보 보기">
										이동
									</Button>
								</Col>
							</Row>
						</Card>
					)}

					{/* 주소 정보 섹션 */}
					<Card
						title={
							<Space>
								<HomeOutlined />
								주소 정보
							</Space>
						}
						className="member-detail__card">
						<Row gutter={[24, 16]}>
							<Col xs={24} sm={12}>
								<InfoItem
									label="주소"
									value={
										(memberData.address
											? memberData.address
											: "-") +
										(memberData.address_detail
											? " " +
											  memberData.address_detail
											: "")
									}
								/>
							</Col>
						</Row>
					</Card>

					{/* 교회 정보 섹션 */}
					<Card
						title={
							<Space>
								<TeamOutlined />
								교회 정보
							</Space>
						}
						className="member-detail__card">
						<Row gutter={[24, 16]}>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
									}}>
									<InfoItem
										label="분류"
										value={
											memberData.classification_name
										}
										tagColor={getClassificationColor(
											memberData.classification_name
										)}
										isTag={
											true
										}
									/>
									<Button
										type="text"
										size="small"
										icon={
											<HistoryOutlined />
										}
										onClick={() =>
											setClassificationRecordsVisible(
												true
											)
										}
										title="분류 변경 기록 보기"
									/>
								</div>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
									}}>
									<InfoItem
										label="상태"
										value={
											memberData.status_name
										}
										tagColor={getStatusColor(
											memberData.status_name
										)}
										isTag={
											true
										}
									/>
									<Button
										type="text"
										size="small"
										icon={
											<HistoryOutlined />
										}
										onClick={() =>
											setStatusRecordsVisible(
												true
											)
										}
										title="상태 변경 기록 보기"
									/>
								</div>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="등록일"
									value={
										memberData.date_of_registration
									}
								/>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
									}}>
									<InfoItem
										label="세례상태"
										value={
											memberData.baptismal_status
										}
									/>
									<Button
										type="text"
										size="small"
										icon={
											<HistoryOutlined />
										}
										onClick={() =>
											setBaptismalStatusRecordsVisible(
												true
											)
										}
										title="세례 상태 변경 기록 보기"
									/>
								</div>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
									}}>
									<InfoItem
										label="징계상태"
										value={
											memberData.disciplinary_status
										}
									/>
									<Button
										type="text"
										size="small"
										icon={
											<HistoryOutlined />
										}
										onClick={() =>
											setDisciplinaryRecordsVisible(
												true
											)
										}
										title="징계 기록 보기"
									/>
								</div>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
									}}>
									<InfoItem
										label="이명기록"
										value="이명 기록 보기"
									/>
									<Button
										type="text"
										size="small"
										icon={
											<HistoryOutlined />
										}
										onClick={() =>
											setTransferRecordsVisible(
												true
											)
										}
										title="이명 기록 보기"
									/>
								</div>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={8}>
								<InfoItem
									label="별세일"
									value={
										memberData.date_of_passing
											? memberData.date_of_passing
											: "-"
									}
								/>
							</Col>
						</Row>
					</Card>

					{/* 소속 정보 섹션 */}
					<Card
						title={
							<Space>
								<TeamOutlined />
								소속 정보
							</Space>
						}
						className="member-detail__card">
						<Row gutter={[24, 16]}>
							{memberData.district_name && (
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}>
										<InfoItem
											label="구역"
											value={
												memberData.district_name +
												"대 교구 " +
												memberData.zone_name +
												"지구 " +
												memberData.cell_name +
												"구역"
											}
										/>
										<Button
											type="text"
											size="small"
											icon={
												<HistoryOutlined />
											}
											onClick={() =>
												setCellRecordsVisible(
													true
												)
											}
											title="셀 변경 기록 보기"
										/>
									</div>
								</Col>
							)}
							{memberData.affiliated_department_name && (
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}>
										<InfoItem
											label="소속기관"
											value={
												memberData.affiliated_department_name
											}
										/>
										<Button
											type="text"
											size="small"
											icon={
												<HistoryOutlined />
											}
											onClick={() =>
												setDepartmentRecordsVisible(
													true
												)
											}
											title="소속기관 변경 기록 보기"
										/>
									</div>
								</Col>
							)}
						</Row>
					</Card>

					{/* 봉사기관 정보 섹션 */}
					<Card
						title={
							<Space>
								<TeamOutlined />
								봉사기관 정보
							</Space>
						}
						className="member-detail__card">
						<Row gutter={[24, 16]}>
							{memberData.serving_departments &&
							memberData
								.serving_departments
								.length > 0 ? (
								memberData.serving_departments.map(
									(
										dept,
										index
									) => (
										<Col
											key={
												dept.id
											}
											xs={
												24
											}
											sm={
												12
											}
											md={
												8
											}>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: "8px",
												}}>
												<InfoItem
													label={`봉사기관 ${
														index +
														1
													}`}
													value={
														dept.department_name
													}
												/>
												<Tag
													style={{
														marginBottom:
															"8px",
													}}
													color={
														dept.status ===
														"활동"
															? "green"
															: dept.status ===
															  "휴직"
															? "orange"
															: dept.status ===
															  "복직"
															? "blue"
															: "default"
													}
													size="small">
													{
														dept.status
													}
												</Tag>
											</div>
										</Col>
									)
								)
							) : (
								<Col span={24}>
									<Text type="secondary">
										등록된
										봉사기관이
										없습니다.
									</Text>
								</Col>
							)}
						</Row>
						<Row style={{ marginTop: 16 }}>
							<Col span={24}>
								<Button
									type="text"
									size="small"
									icon={
										<HistoryOutlined />
									}
									onClick={() =>
										setServingDepartmentRecordsVisible(
											true
										)
									}
									title="봉사기관 변경 기록 보기">
									봉사기관
									변경
									기록
									보기
								</Button>
							</Col>
						</Row>
					</Card>

					{/* 기타 정보 섹션 */}
					{memberData.etc && (
						<Card
							title={
								<Space>
									<FileTextOutlined />
									기타
									정보
								</Space>
							}
							className="member-detail__card">
							<Row>
								<Col span={24}>
									<InfoItem
										label="메모"
										value={
											memberData.etc
										}
										isMultiline={
											true
										}
									/>
								</Col>
							</Row>
						</Card>
					)}

					{/* 가족 관계도 섹션 */}
					<Card
						title={
							<Space>
								<HeartOutlined />
								가족 관계도
							</Space>
						}
						className="member-detail__card">
						<div
							style={{
								padding: "20px 0",
							}}>
							{/* Parents Row - Member's Parents and Spouse's Parents */}
							{(sortedParents.length >
								0 ||
								sortedSpouseParents.length >
									0) && (
								<Row
									justify="center"
									align="middle"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent:
											"center",
										marginBottom:
											"30px",
									}}>
									{/* First group: Member's parents (if male) or Spouse's parents (if female) */}
									{memberData.gender ===
									"남" ? (
										// Male member: Member's parents on left, Spouse's parents on right
										<>
											{/* Member's Parents */}
											{sortedParents.length >
												0 && (
												<Col
													style={{
														marginRight:
															sortedSpouseParents.length >
															0
																? "40px"
																: "0",
														textAlign: "center",
													}}>
													<Text
														style={{
															fontSize: "12px",
															color: "#666",
															fontStyle: "italic",
															marginBottom:
																"10px",
															display: "block",
														}}>
														본인
														부모
													</Text>
													<Row
														justify="center"
														align="middle">
														{sortedParents.map(
															(
																parent,
																index
															) => (
																<>
																	<Col
																		key={
																			parent.id
																		}
																		style={{
																			display: "flex",
																			alignItems: "center",
																			justifyContent:
																				"center",
																			gap: "8px",
																		}}>
																		<FamilyMemberCard
																			member={
																				parent
																			}
																			relationship="부모"
																			onClick={() =>
																				handleFamilyMemberClick(
																					parent.id
																				)
																			}
																		/>
																	</Col>
																	{/* Add spouse connector between parents */}
																	{index ===
																		0 &&
																		sortedParents.length >
																			1 && (
																			<div className="spouse-connector" />
																		)}
																</>
															)
														)}
													</Row>
												</Col>
											)}

											{/* Spouse's Parents */}
											{sortedSpouseParents.length >
												0 && (
												<Col
													style={{
														marginLeft:
															sortedParents.length >
															0
																? "40px"
																: "0",
														textAlign: "center",
													}}>
													<Text
														style={{
															fontSize: "12px",
															color: "#666",
															fontStyle: "italic",
															marginBottom:
																"10px",
															display: "block",
														}}>
														배우자
														부모
													</Text>
													<Row
														style={{
															display: "flex",
															alignItems: "center",
															justifyContent:
																"center",
														}}>
														{sortedSpouseParents.map(
															(
																parent,
																index
															) => (
																<>
																	<Col
																		key={
																			parent.id
																		}>
																		<FamilyMemberCard
																			member={
																				parent
																			}
																			relationship="배우자부모"
																			onClick={() =>
																				handleFamilyMemberClick(
																					parent.id
																				)
																			}
																		/>
																	</Col>
																	{/* Add spouse connector between parents */}
																	{index ===
																		0 &&
																		sortedSpouseParents.length >
																			1 && (
																			<div className="spouse-connector" />
																		)}
																</>
															)
														)}
													</Row>
												</Col>
											)}
										</>
									) : (
										// Female member: Spouse's parents on left, Member's parents on right
										<>
											{/* Spouse's Parents */}
											{sortedSpouseParents.length >
												0 && (
												<Col
													style={{
														marginRight:
															sortedParents.length >
															0
																? "40px"
																: "0",
														textAlign: "center",
													}}>
													<Text
														style={{
															fontSize: "12px",
															color: "#666",
															fontStyle: "italic",
															marginBottom:
																"10px",
															display: "block",
														}}>
														배우자
														부모
													</Text>
													<Row
														style={{
															display: "flex",
															alignItems: "center",
															justifyContent:
																"center",
														}}>
														{sortedSpouseParents.map(
															(
																parent,
																index
															) => (
																<>
																	<Col
																		key={
																			parent.id
																		}>
																		<FamilyMemberCard
																			member={
																				parent
																			}
																			relationship="배우자부모"
																			onClick={() =>
																				handleFamilyMemberClick(
																					parent.id
																				)
																			}
																		/>
																	</Col>
																	{/* Add spouse connector between parents */}
																	{index ===
																		0 &&
																		sortedSpouseParents.length >
																			1 && (
																			<div className="spouse-connector" />
																		)}
																</>
															)
														)}
													</Row>
												</Col>
											)}

											{/* Member's Parents */}
											{sortedParents.length >
												0 && (
												<Col
													style={{
														marginLeft:
															sortedSpouseParents.length >
															0
																? "40px"
																: "0",
														textAlign: "center",
													}}>
													<Text
														style={{
															fontSize: "12px",
															color: "#666",
															fontStyle: "italic",
															marginBottom:
																"10px",
															display: "block",
														}}>
														본인
														부모
													</Text>
													<Row
														justify="center"
														align="middle">
														{sortedParents.map(
															(
																parent,
																index
															) => (
																<>
																	<Col
																		key={
																			parent.id
																		}
																		style={{
																			display: "flex",
																			alignItems: "center",
																			justifyContent:
																				"center",
																			gap: "8px",
																		}}>
																		<FamilyMemberCard
																			member={
																				parent
																			}
																			relationship="부모"
																			onClick={() =>
																				handleFamilyMemberClick(
																					parent.id
																				)
																			}
																		/>
																	</Col>
																	{/* Add spouse connector between parents */}
																	{index ===
																		0 &&
																		sortedParents.length >
																			1 && (
																			<div className="spouse-connector" />
																		)}
																</>
															)
														)}
													</Row>
												</Col>
											)}
										</>
									)}
								</Row>
							)}

							{/* Current User and Spouse Row */}
							<Row
								justify="center"
								align="middle"
								style={{
									marginBottom:
										"30px",
								}}>
								{/* If user is male, show spouse on right; if female, show spouse on left */}
								{memberData.spouse &&
									memberData.gender ===
										"여" && (
										<Col>
											<FamilyMemberCard
												member={
													memberData.spouse
												}
												relationship="배우자"
												onClick={() =>
													handleFamilyMemberClick(
														memberData
															.spouse
															.id
													)
												}
											/>
										</Col>
									)}

								{/* Spouse connector line */}
								{memberData.spouse &&
									memberData.gender ===
										"여" && (
										<div className="spouse-connector"></div>
									)}

								{/* Current User */}
								<Col>
									<Card
										size="small"
										className="family-member-card current-user"
										style={{
											border: "2px solid #2457e6",
											backgroundColor:
												"#f0f8ff",
										}}>
										<div
											style={{
												textAlign: "center",
											}}>
											<Avatar
												size={
													50
												}
												icon={
													<UserOutlined />
												}
												style={{
													backgroundColor:
														memberData.gender ===
														"남"
															? "#2457e6"
															: "#eb2f96",
													marginBottom: 8,
												}}
											/>
											<div
												style={{
													fontSize: "14px",
													fontWeight: "bold",
												}}>
												{
													memberData.name
												}
											</div>
											<div
												style={{
													fontSize: "12px",
													color: "#666",
												}}>
												{memberData.birthdate
													? new Date(
															memberData.birthdate
													  ).toLocaleDateString(
															"ko-KR"
													  )
													: "-"}
											</div>
											<Tag
												size="small"
												color="green">
												본인
											</Tag>
										</div>
									</Card>
								</Col>

								{/* Spouse connector line for male user */}
								{memberData.spouse &&
									memberData.gender ===
										"남" && (
										<div className="spouse-connector"></div>
									)}

								{/* If user is male, show spouse on right */}
								{memberData.spouse &&
									memberData.gender ===
										"남" && (
										<Col>
											<FamilyMemberCard
												member={
													memberData.spouse
												}
												relationship="배우자"
												onClick={() =>
													handleFamilyMemberClick(
														memberData
															.spouse
															.id
													)
												}
											/>
										</Col>
									)}
							</Row>

							{/* Children Row */}
							{sortedChildren.length >
								0 && (
								<Row justify="center">
									{sortedChildren.map(
										(
											child,
											index
										) => (
											<Col
												key={
													child.id
												}
												style={{
													margin: "0 10px",
												}}>
												<ChildWithSpouseCard
													child={
														child
													}
													onClick={
														handleFamilyMemberClick
													}
												/>
											</Col>
										)
									)}
								</Row>
							)}

							{/* No family members message */}
							{sortedParents.length ===
								0 &&
								!memberData.spouse &&
								sortedChildren.length ===
									0 &&
								sortedSpouseParents.length ===
									0 && (
									<div
										style={{
											textAlign: "center",
											color: "#999",
											padding: "40px 0",
										}}>
										<Text>
											등록된
											가족
											관계가
											없습니다.
										</Text>
									</div>
								)}
						</div>
					</Card>
				</div>
			</div>

			{/* Record View Modals */}
			<ViewClassificationChangeRecordsModal
				visible={classificationRecordsVisible}
				onCancel={() =>
					setClassificationRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewStatusChangeRecordsModal
				visible={statusRecordsVisible}
				onCancel={() => setStatusRecordsVisible(false)}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewBaptismalStatusChangeRecordsModal
				visible={baptismalStatusRecordsVisible}
				onCancel={() =>
					setBaptismalStatusRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewCellChangeRecordsModal
				visible={cellRecordsVisible}
				onCancel={() => setCellRecordsVisible(false)}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewAffiliatedDepartmentChangeRecordsModal
				visible={departmentRecordsVisible}
				onCancel={() =>
					setDepartmentRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewDisciplinaryRecordsModal
				visible={disciplinaryRecordsVisible}
				onCancel={() =>
					setDisciplinaryRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewServingDepartmentChangeRecordsModal
				visible={servingDepartmentRecordsVisible}
				onCancel={() =>
					setServingDepartmentRecordsVisible(
						false
					)
				}
				userId={id}
				userName={memberData?.name}
			/>
			<ViewTransferRecordsModal
				visible={transferRecordsVisible}
				onCancel={() =>
					setTransferRecordsVisible(false)
				}
				userId={id}
				userName={memberData?.name}
			/>
		</>
	);
};

export default ViewMemberDetail;
